import { useEffect, useMemo, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import type { DateRange } from "react-day-picker";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  AlertCircle,
  ArrowLeft,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Power,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DateRangeFilter } from "@/components/ui/date-range-filter";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  DetailRow,
  DetailSection,
  EmptyState,
  LoadingState,
} from "@/components/shared/detail-view";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateMaintenanceWindow,
  useDisableMaintenanceWindow,
  useMaintenanceWindowDetail,
  useMaintenanceWindows,
  useUpdateMaintenanceWindow,
} from "@/hooks/useMaintenanceWindows";
import { useConfirmUnsaved, useDirtyFlag } from "@/hooks/useUnsavedChanges";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { useUnsavedChangesStore } from "@/stores/unsavedChangesStore";
import type {
  MaintenanceWindow,
  MaintenanceWindowFilters,
  MaintenanceWindowPayload,
} from "@/types/maintenance-windows";

type MaintenanceWindowView = "list" | "view" | "add";

const PAGE_SIZE = 20;

// A window has exactly two states: enabled or disabled (`is_active`).
const STATE_TABS = [
  { value: "all", label: "All" },
  { value: "true", label: "Enabled" },
  { value: "false", label: "Disabled" },
] as const;

const enabledTone = (isActive: boolean) =>
  isActive ? "bg-success/10 text-success" : "bg-muted text-muted-foreground";

const formatDateTime = (value?: string | null) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatDate = (value?: string | null) => {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium" }).format(
    new Date(value)
  );
};

const toDateParam = (value?: Date) =>
  value ? format(value, "yyyy-MM-dd") : undefined;

const StateBadge = ({ isActive }: { isActive: boolean }) => (
  <Badge variant={isActive ? "success" : "neutral"}>
    {isActive ? "Enabled" : "Disabled"}
  </Badge>
);

export const MaintenanceWindowsHome = () => {
  const [view, setView] = useState<MaintenanceWindowView>("list");
  const [selectedWindowId, setSelectedWindowId] = useState<
    number | undefined
  >();
  const confirmUnsaved = useConfirmUnsaved();
  const { hasPermission } = useAuthStore();
  const canManageMaintenance = hasPermission(
    CONSOLE_PERMISSIONS.MANAGE_MAINTENANCE
  );

  const openList = () => {
    confirmUnsaved(() => {
      setSelectedWindowId(undefined);
      setView("list");
    });
  };

  const openDetail = (windowId: number) => {
    confirmUnsaved(() => {
      setSelectedWindowId(windowId);
      setView("view");
    });
  };

  const openAdd = () => {
    if (!canManageMaintenance) return;
    confirmUnsaved(() => {
      setSelectedWindowId(undefined);
      setView("add");
    });
  };

  if (view === "view" && selectedWindowId) {
    return (
      <MaintenanceWindowDetail
        windowId={selectedWindowId}
        onBack={openList}
        canManageMaintenance={canManageMaintenance}
      />
    );
  }

  if (view === "add" && canManageMaintenance) {
    return <MaintenanceWindowForm onBack={openList} />;
  }

  return (
    <MaintenanceWindowList
      onAddNew={openAdd}
      onView={openDetail}
      canManageMaintenance={canManageMaintenance}
    />
  );
};

const MaintenanceWindowList = ({
  onAddNew,
  onView,
  canManageMaintenance,
}: {
  onAddNew: () => void;
  onView: (windowId: number) => void;
  canManageMaintenance: boolean;
}) => {
  const [stateFilter, setStateFilter] = useState("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [stateFilter, dateRange]);

  const filters: MaintenanceWindowFilters = useMemo(
    () => ({
      page: currentPage,
      page_size: PAGE_SIZE,
      is_active: stateFilter === "all" ? undefined : stateFilter === "true",
      date_from: toDateParam(dateRange?.from),
      date_to: toDateParam(dateRange?.to),
    }),
    [currentPage, dateRange, stateFilter]
  );

  const { data, isLoading, isError } = useMaintenanceWindows(filters);

  const windows = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const hasNext = !!data?.next;
  const hasPrevious = !!data?.previous;
  const hasActiveFilters = stateFilter !== "all" || Boolean(dateRange);

  const clearFilters = () => {
    setStateFilter("all");
    setDateRange(undefined);
    setCurrentPage(1);
  };

  return (
    <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none">
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="z-h1 text-foreground">
              Maintenance Window
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Schedule and disable planned website maintenance windows from the
              console.
            </CardDescription>
          </div>
          {canManageMaintenance && (
            <Button id="maintenance-window-add-btn" onClick={onAddNew}>
              <Plus className="mr-2 h-4 w-4" />
              New window
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1 min-h-0 flex flex-col gap-4 p-0">
        {/* State tabs */}
        <div className="px-6">
          <Tabs value={stateFilter} onValueChange={setStateFilter}>
            <TabsList variant="line">
              {STATE_TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-6">
          <div className="flex items-center gap-2 flex-wrap">
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              placeholder="Window dates"
            />
            {hasActiveFilters && (
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={clearFilters}
                className="z-caption h-auto p-0 text-muted-foreground hover:text-foreground"
              >
                Clear all
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 px-6 flex flex-col">
          {isLoading ? (
            <LoadingState label="Loading maintenance windows..." />
          ) : isError ? (
            <EmptyState
              icon={AlertCircle}
              title="Failed to load maintenance windows"
              description="Please try again in a moment."
            />
          ) : windows.length === 0 ? (
            <EmptyState
              icon={CalendarClock}
              title="No maintenance windows found"
              description={
                hasActiveFilters
                  ? "Try adjusting your filters."
                  : "Schedule a window before planned downtime."
              }
            />
          ) : (
            <div className="flex-1 min-h-0 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-sm">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow className="hover:bg-card">
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground pl-4">
                      Window
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      Schedule
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground">
                      State
                    </TableHead>
                    <TableHead className="z-label uppercase tracking-wide text-muted-foreground pr-4" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {windows.map((window) => (
                    <TableRow
                      key={window.id}
                      className="group bg-card transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => onView(window.id)}
                    >
                      <TableCell className="max-w-[360px] pl-4">
                        <div className="min-w-0">
                          <p className="z-numeric text-foreground">
                            Window #{window.id}
                          </p>
                          {window.description ? (
                            <p className="z-caption truncate text-muted-foreground">
                              {window.description}
                            </p>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="z-caption text-muted-foreground whitespace-nowrap">
                        {formatDate(window.start_time)}
                        <span className="px-1">→</span>
                        {formatDate(window.end_time)}
                      </TableCell>
                      <TableCell>
                        <StateBadge isActive={window.is_active} />
                      </TableCell>
                      <TableCell className="pr-4 text-right">
                        <Button
                          id={`view-maintenance-window-${window.id}`}
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto gap-0.5 p-0 text-primary"
                          onClick={(event) => {
                            event.stopPropagation();
                            onView(window.id);
                          }}
                        >
                          Details
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Footer */}
        {totalCount > 0 ? (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <span className="z-secondary text-muted-foreground">
              Showing{" "}
              <span className="z-numeric font-medium text-foreground">
                {(currentPage - 1) * PAGE_SIZE + 1}-
                {Math.min(currentPage * PAGE_SIZE, totalCount)}
              </span>{" "}
              of{" "}
              <span className="z-numeric font-medium text-foreground">
                {totalCount}
              </span>{" "}
              windows
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasPrevious || isLoading}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                >
                  <ChevronLeft className="mr-1 h-4 w-4" />
                  Previous
                </Button>
                <span className="z-secondary text-muted-foreground px-2">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasNext || isLoading}
                  onClick={() => setCurrentPage((page) => page + 1)}
                >
                  Next
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

const MaintenanceWindowDetail = ({
  windowId,
  onBack,
  canManageMaintenance,
}: {
  windowId: number;
  onBack: () => void;
  canManageMaintenance: boolean;
}) => {
  const { toast } = useToast();
  const [manageOpen, setManageOpen] = useState(false);
  const {
    data: window,
    isLoading,
    isError,
  } = useMaintenanceWindowDetail(windowId);
  const disableMutation = useDisableMaintenanceWindow();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !window) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-20 text-muted-foreground">
        <CalendarClock className="mb-2 h-10 w-10 opacity-40" />
        <p className="z-body font-medium">
          Failed to load maintenance window details.
        </p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Go Back
        </Button>
      </div>
    );
  }

  const handleDisable = async () => {
    try {
      await disableMutation.mutateAsync(window.id);
      toast({ title: "Maintenance window disabled." });
    } catch {
      toast({
        title: "Could not disable window",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  };

  const tone = enabledTone(window.is_active);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-6">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-1">
        {/* Back + actions */}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="-ml-2 gap-1 px-2 text-primary hover:bg-primary/5 hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          {canManageMaintenance && (
            <div className="flex items-center gap-2">
              {window.is_active && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={disableMutation.isPending}
                      className="text-destructive hover:text-destructive"
                    >
                      <Power className="mr-2 h-4 w-4" />
                      Disable
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Disable maintenance window?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This turns off the scheduled maintenance window without
                        deleting it. It can be re-enabled later by editing the
                        window.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={disableMutation.isPending}>
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(event) => {
                          event.preventDefault();
                          void handleDisable();
                        }}
                      >
                        Disable window
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Button size="sm" onClick={() => setManageOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            </div>
          )}
        </div>

        {/* Hero — the window, when it runs, and whether it's live */}
        <Card className="border border-border/60 shadow-sm">
          <CardContent className="flex items-center gap-4 px-6 py-5">
            <div
              className={cn(
                "flex h-14 w-14 shrink-0 items-center justify-center rounded-lg",
                tone
              )}
            >
              <CalendarClock className="h-7 w-7" strokeWidth={2} />
            </div>
            <div className="min-w-0 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2.5">
                <p className="z-h1 text-foreground">Window #{window.id}</p>
                <StateBadge isActive={window.is_active} />
              </div>
              <p className="z-caption text-muted-foreground">
                {formatDateTime(window.start_time)}
                <span className="px-1.5">→</span>
                {formatDateTime(window.end_time)}
              </p>
              {window.description ? (
                <p className="z-caption text-muted-foreground">
                  {window.description}
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <DetailSection title="Schedule">
          <DetailRow label="Starts" value={formatDateTime(window.start_time)} />
          <DetailRow label="Ends" value={formatDateTime(window.end_time)} />
        </DetailSection>

        {/* Configuration */}
        <DetailSection title="Configuration">
          <DetailRow
            label="State"
            value={window.is_active ? "Enabled" : "Disabled"}
          />
          <DetailRow
            label="Description"
            value={window.description || undefined}
          />
        </DetailSection>

        {/* Activity */}
        <DetailSection title="Activity">
          <DetailRow
            label="Created"
            value={formatDateTime(window.created_at)}
          />
          <DetailRow
            label="Updated"
            value={formatDateTime(window.updated_at)}
          />
        </DetailSection>
      </div>

      {canManageMaintenance && (
        <ManageMaintenanceWindowDialog
          window={window}
          open={manageOpen}
          onOpenChange={setManageOpen}
        />
      )}
    </div>
  );
};

const FIELD_LABEL = "z-label mb-1 text-muted-foreground";

const RequiredMark = () => <span className="text-destructive"> *</span>;

const FormSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Card className="border border-border/60 shadow-sm">
    <CardContent className="space-y-3 p-4">
      <p className="z-eyebrow uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      {children}
    </CardContent>
  </Card>
);

const DESCRIPTION_MAX = 280;

const requiredText = (label: string) =>
  z.string().trim().min(1, `${label} is required`);

const formSchema = z
  .object({
    start_time: z.string().min(1, "Start time is required"),
    end_time: z.string().min(1, "End time is required"),
    description: z
      .string()
      .max(
        DESCRIPTION_MAX,
        `Description must be ${DESCRIPTION_MAX} characters or fewer`
      )
      .optional(),
    is_active: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (!values.start_time || !values.end_time) return;
    if (new Date(values.end_time) <= new Date(values.start_time)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End time must be after start time",
        path: ["end_time"],
      });
    }
  });

type MaintenanceWindowFormValues = z.infer<typeof formSchema>;

const buildDefaultValues = (
  window?: MaintenanceWindow
): MaintenanceWindowFormValues => ({
  start_time: window?.start_time ?? "",
  end_time: window?.end_time ?? "",
  description: window?.description ?? "",
  is_active: window?.is_active ?? true,
});

const buildPayload = (
  values: MaintenanceWindowFormValues
): MaintenanceWindowPayload => ({
  start_time: new Date(values.start_time).toISOString(),
  end_time: new Date(values.end_time).toISOString(),
  description: values.description?.trim() ?? "",
  is_active: values.is_active,
});

// Create is a full page (room to set the whole schedule); edits happen in the
// limited ManageMaintenanceWindowDialog below.
const MaintenanceWindowForm = ({ onBack }: { onBack: () => void }) => {
  const { toast } = useToast();
  const confirmUnsaved = useConfirmUnsaved();
  const clearDirty = useUnsavedChangesStore((s) => s.clearKey);
  const createMutation = useCreateMaintenanceWindow();

  const form = useForm<MaintenanceWindowFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: buildDefaultValues(),
    mode: "onChange",
  });

  const { isDirty } = form.formState;
  const dirtyKey = "maintenance-window:new";
  useDirtyFlag(dirtyKey, isDirty, "Add Maintenance Window");

  const onSubmit = async (values: MaintenanceWindowFormValues) => {
    try {
      await createMutation.mutateAsync(buildPayload(values));
      toast({ title: "Maintenance window created." });
      clearDirty(dirtyKey);
      onBack();
    } catch {
      toast({
        title: "Something went wrong",
        description: "Please check the window details and try again.",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => confirmUnsaved(onBack);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto pb-6">
      <div className="mx-auto w-full max-w-3xl space-y-4 px-1">
        {/* Back + actions */}
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            className="-ml-2 gap-1 px-2 text-primary hover:bg-primary/5 hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </Button>
          <div className="flex items-center gap-2">
            <Button
              id="cancel-maintenance-window-btn"
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              id="save-maintenance-window-btn"
              type="submit"
              form="maintenance-window-form"
              size="sm"
              disabled={createMutation.isPending}
              className="min-w-28"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Submit"
              )}
            </Button>
          </div>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <h2 className="z-h2 text-foreground">New maintenance window</h2>
        </div>

        <Form {...form}>
          <form
            id="maintenance-window-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-3"
          >
            {/* Enabled — top */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 space-y-0 rounded-lg border border-border/60 bg-card px-4 py-3 shadow-sm">
                  <div className="space-y-0.5">
                    <FormLabel className="z-label mb-0">Enabled</FormLabel>
                    <p className="z-caption text-muted-foreground">
                      {field.value
                        ? "Window will take effect during its scheduled time"
                        : "Created disabled — won't take effect until enabled"}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Schedule */}
            <FormSection title="Schedule">
              <div className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="start_time"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        Start time
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <DateTimePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Pick start date & time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="end_time"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <FormLabel className={FIELD_LABEL}>
                        End time
                        <RequiredMark />
                      </FormLabel>
                      <FormControl>
                        <DateTimePicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Pick end date & time"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </FormSection>

            {/* Details */}
            <FormSection title="Details">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <FormLabel className={FIELD_LABEL}>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={3}
                        maxLength={DESCRIPTION_MAX}
                        placeholder="Optional operational note"
                      />
                    </FormControl>
                    <div className="flex items-center justify-between gap-2">
                      <FormMessage />
                      <span className="z-hint ml-auto text-muted-foreground">
                        {field.value?.length ?? 0}/{DESCRIPTION_MAX}
                      </span>
                    </div>
                  </FormItem>
                )}
              />
            </FormSection>
          </form>
        </Form>
      </div>
    </div>
  );
};

type ManageFormValues = {
  is_active: boolean;
  start_time: string;
  end_time: string;
  description?: string;
};

// Start time can only move while the window is still upcoming. Once it has
// started, it's fixed — mirror this on the backend (see ZCS Jira).
const makeManageSchema = (window: MaintenanceWindow) =>
  z
    .object({
      is_active: z.boolean(),
      start_time: requiredText("Start time"),
      end_time: requiredText("End time"),
      description: z
        .string()
        .max(
          DESCRIPTION_MAX,
          `Description must be ${DESCRIPTION_MAX} characters or fewer`
        )
        .optional(),
    })
    .superRefine((values, ctx) => {
      const started = new Date(window.start_time) <= new Date();
      // Guard against a tampered/stale start when the window has begun.
      if (
        started &&
        new Date(values.start_time).getTime() !==
          new Date(window.start_time).getTime()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Start time can't be changed once the window has started",
          path: ["start_time"],
        });
      }
      if (new Date(values.end_time) <= new Date(values.start_time)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "End time must be after start time",
          path: ["end_time"],
        });
      }
    });

const manageDefaults = (window: MaintenanceWindow): ManageFormValues => ({
  is_active: window.is_active,
  start_time: window.start_time,
  end_time: window.end_time,
  description: window.description ?? "",
});

const ManageMaintenanceWindowDialog = ({
  window,
  open,
  onOpenChange,
}: {
  window: MaintenanceWindow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { toast } = useToast();
  const updateMutation = useUpdateMaintenanceWindow();

  const form = useForm<ManageFormValues>({
    resolver: zodResolver(makeManageSchema(window)),
    defaultValues: manageDefaults(window),
    mode: "onChange",
  });

  useEffect(() => {
    if (open) form.reset(manageDefaults(window));
  }, [open, window, form]);

  // Once started, the start time is fixed — only the end, note, and enabled
  // state remain editable.
  const hasStarted = new Date(window.start_time) <= new Date();

  const onSubmit = async (values: ManageFormValues) => {
    const payload: MaintenanceWindowPayload = {
      start_time: new Date(values.start_time).toISOString(),
      end_time: new Date(values.end_time).toISOString(),
      description: values.description?.trim() ?? "",
      is_active: values.is_active,
    };
    try {
      await updateMutation.mutateAsync({ windowId: window.id, payload });
      toast({ title: "Maintenance window updated." });
      onOpenChange(false);
    } catch {
      toast({
        title: "Update failed",
        description: "Please try again in a moment.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Window #{window.id}</DialogTitle>
          <DialogDescription>
            {hasStarted
              ? "This window has started, so its start time is fixed. You can still extend the end, edit the note, or disable it."
              : "Adjust the schedule, note, or enabled state. The start time locks once the window begins."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between gap-4 rounded-lg border border-border/60 px-4 py-3">
                  <div className="space-y-0.5">
                    <FormLabel className="z-label mb-0">Enabled</FormLabel>
                    <p className="z-caption text-muted-foreground">
                      {field.value
                        ? "Window can take effect during its scheduled time"
                        : "Window is disabled — won't take effect"}
                    </p>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="start_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">Start time</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      disabled={hasStarted}
                      placeholder="Pick start date & time"
                    />
                  </FormControl>
                  {hasStarted ? (
                    <p className="z-hint text-muted-foreground">
                      Locked — the window has already started.
                    </p>
                  ) : (
                    <FormMessage />
                  )}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="end_time"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">End time</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Pick end date & time"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="z-label">Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      rows={3}
                      maxLength={DESCRIPTION_MAX}
                      placeholder="Optional operational note"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Save changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
